// src/utils/TMapUtils.js

// 天地图API缓存
let tMapPromise = null;

// 默认城市信息
const DEFAULT_CITY = {
  name: "北京",
  code: "110100"
};

/**
 * 初始化天地图API
 * @returns {Promise} 返回天地图API对象
 */
function initTMap() {
  if (!tMapPromise) {
    tMapPromise = new Promise((resolve, reject) => {
      if (window.T) {
        console.log('地理信息已加载');
        resolve(window.T);
        return;
      }
      
      // 轮询等待天地图API加载完成
      let checkCount = 0;
      const checkT = () => {
        if (window.T) {
          resolve(window.T);
        } else if (checkCount > 30) { // 最多等待3秒
          reject(new Error("地理信息加载超时"));
        } else {
          checkCount++;
          setTimeout(checkT, 100);
        }
      };
      checkT();
    });
  }
  return tMapPromise;
}

/**
 * 获取当前位置
 * @returns {Promise} 返回经纬度对象
 */
async function getCurrentPosition() {
  try {
    const T = await initTMap();
    
    return new Promise((resolve, reject) => {
      if (!T.Geolocation) {
        reject(new Error("定位服务不可用"));
        return;
      }
      
      const geolocation = new T.Geolocation();
      geolocation.getCurrentPosition(
        (position) => {
          console.log('定位坐标', position);
          if (position && position.lnglat) {
            resolve(position.lnglat);
          } else {
            reject(new Error("获取位置信息失败"));
          }
        },
        (error) => {
          reject(new Error("定位服务错误"));
        }
      );
    });
  } catch (error) {
    throw new Error("定位初始化失败");
  }
}

/**
 * 逆地理编码：根据经纬度获取地址
 * @param {Object|String} lngLat 经纬度对象或经纬度字符串
 * @returns {Promise} 返回地址信息
 */
async function reverseGeocoder(lngLat) {
  try {
    const T = await initTMap();
    
    // 统一经纬度格式
    let tLngLat = lngLat;
    if (typeof lngLat === 'string') {
      const [lon, lat] = lngLat.split(',');
      tLngLat = new T.LngLat(lon, lat);
    }
    
    return new Promise((resolve, reject) => {
      new T.Geocoder().getLocation(tLngLat, (location) => {
        if (location && location.status === '0') {
          resolve(location);
        } else {
          reject(new Error("地址解析失败"));
        }
      });
    });
  } catch (error) {
    throw new Error("地址解析服务不可用");
  }
}

/**
 * 地理编码：根据地址获取经纬度
 * @param {String} address 地址字符串
 * @returns {Promise} 返回经纬度信息
 */
async function tMapLocationPoint(address) {
  try {
    const T = await initTMap();
    
    return new Promise((resolve, reject) => {
      new T.Geocoder().getPoint(address, (point) => {
        console.log('地址:{}地理编码坐标:{}', address, point);
        if (point && point.status === '0') {
          resolve(point.location);
        } else {
          reject(new Error("地址解析失败"));
        }
      });
    });
  } catch (error) {
    throw new Error("地址解析服务不可用");
  }
}

/**
 * 根据关键字搜索位置
 * @param {Number} lng 经度
 * @param {Number} lat 纬度
 * @param {String} keywords 关键字
 * @param {String} divId 地图容器ID
 * @param {Function} callback 回调函数
 */
async function tMapLocalSearch(lng, lat, keywords, divId, callback) {
  if (!keywords || !lng || !lat || !divId || !callback) {
    throw new Error("搜索参数不完整");
  }

  try {
    const T = await initTMap();
    
    const tMap = new T.Map(divId);
    tMap.centerAndZoom(new T.LngLat(lng, lat), 12);
    
    const localSearch = new T.LocalSearch(tMap, {
      pageCapacity: 10,
      onSearchComplete: (result) => {
        console.log('建议词结果集', result);
        tMap.clearOverLays();
        callback(result.suggests || []);
      }
    });
    
    localSearch.search(keywords, 4);
  } catch (error) {
    throw new Error("搜索服务不可用");
  }
}

/**
 * 拉门店 / 影院距离用：优先读已缓存坐标，没有则调天地图定位并写入 localStorage。
 * 不改城市，不挡首页。失败返回 null。
 */
async function ensureUserLngLat() {
  const lat = localStorage.getItem("latitude");
  const lng = localStorage.getItem("longitude");
  if (lat && lng) {
    return { lat, lng };
  }
  try {
    const position = await getCurrentPosition();
    if (position && position.lat != null && position.lng != null) {
      localStorage.setItem("latitude", position.lat);
      localStorage.setItem("longitude", position.lng);
      return { lat: position.lat, lng: position.lng };
    }
  } catch (e) {
    // 定位失败时允许无距离
  }
  return null;
}

export { 
  initTMap, 
  getCurrentPosition, 
  reverseGeocoder, 
  tMapLocationPoint, 
  tMapLocalSearch,
  ensureUserLngLat,
  DEFAULT_CITY
};